/**
 * Progressive image loading hook
 * Shows a low-res thumbnail first, then upgrades to full resolution in the background.
 */

import { useState, useEffect } from 'react';

/**
 * Load a thumbnail immediately, then upgrade to full-res.
 * @param thumbnailUrl Low-res URL (loaded first, shown immediately)
 * @param fullUrl Full-res URL (loaded in background)
 * @returns Current best available image URL
 */
export function useProgressiveImage(
  thumbnailUrl: string | undefined,
  fullUrl: string | undefined,
): { src: string; isLoading: boolean } {
  const [loadedFullUrl, setLoadedFullUrl] = useState<string | undefined>(undefined);
  const [prevFullUrl, setPrevFullUrl] = useState(fullUrl);

  // Reset loaded URL synchronously when fullUrl changes (derived state during render)
  if (fullUrl !== prevFullUrl) {
    setPrevFullUrl(fullUrl);
    if (loadedFullUrl !== undefined) {
      setLoadedFullUrl(undefined);
    }
  }

  useEffect(() => {
    if (!fullUrl) return;

    let cancelled = false;
    const img = new Image();

    const handleLoad = (): void => {
      if (!cancelled) {
        setLoadedFullUrl(fullUrl);
      }
    };

    img.addEventListener('load', handleLoad);
    img.src = fullUrl;

    return () => {
      cancelled = true;
      img.removeEventListener('load', handleLoad);
    };
  }, [fullUrl]);

  const src = loadedFullUrl ?? thumbnailUrl ?? '';
  const isLoading = !!fullUrl && !loadedFullUrl;

  return { src, isLoading };
}
